'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface SignUpStep1 {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
}

interface SignUpStep2 {
  artistName: string;
  country: string;
  phone: string;
}

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'United Arab Emirates', 
  'Canada', 'Australia', 'Germany', 'France', 'Other'
];

const PLANS = [
  {
    id: 'free',
    name: 'Free Starter',
    price: '₹0',
    period: 'forever',
    releases: '5 releases / month',
    features: ['150+ streaming stores', 'Auto ISRC & UPC', '85% Royalty Payout', 'Standard QC'],
    color: 'var(--muted-foreground)',
  },
  {
    id: 'artist_pro',
    name: 'Artist Pro',
    price: '₹1,499',
    period: '/year',
    releases: '1 Artist • Unlimited releases',
    features: ['100% Royalties', 'YouTube Content ID', 'OAC verification', 'Priority 24h QC'],
    color: 'var(--primary)',
    popular: true,
  },
  {
    id: 'label_producer',
    name: 'Label Elite',
    price: '₹3,499',
    period: '/year',
    releases: 'Up to 4 Artists • Unlimited releases',
    features: ['100% Royalties', 'Custom Label Branding', 'Automated Splits', 'Dedicated manager'],
    color: 'var(--accent)',
  },
];

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const router = useRouter();
  const supabase = createClient();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('artist_pro');
  const [step1Data, setStep1Data] = useState<SignUpStep1 | null>(null);
  const [step2Data, setStep2Data] = useState<SignUpStep2 | null>(null);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedDist, setAgreedDist] = useState(false);

  const form1 = useForm<SignUpStep1>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '' }
  });
  const form2 = useForm<SignUpStep2>({
    defaultValues: { artistName: '', country: 'India', phone: '' }
  });

  const handleStep1 = async (data: SignUpStep1) => {
    if (data.password !== data.confirmPassword) {
      form1.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setStep1Data(data);
    setStep(2);
  };

  const handleStep2 = async (data: SignUpStep2) => {
    const rawPhone = data.phone?.replace(/\D/g, '') || '';
    if (rawPhone.length < 10) {
      form2.setError('phone', { message: 'Please enter a valid 10-digit mobile number' });
      return;
    }
    setStep2Data(data);
    setStep(3);
  };

  const handleStep3 = () => {
    setStep(4);
  };

  const handleFinalSubmit = async () => {
    if (!agreedTerms || !agreedDist) {
      toast.error('Please accept all required agreements to continue');
      return;
    }
    if (!step1Data || !step2Data) {
      toast.error('Missing registration data. Please start over.');
      return;
    }

    setIsLoading(true);
    try {
      const cleanPhone = `+91${step2Data.phone.replace(/\D/g, '').slice(-10)}`;

      // 1. Create Supabase Auth User
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: step1Data.email.trim(),
        password: step1Data.password,
        options: {
          data: {
            full_name: step1Data.fullName.trim(),
            artist_name: step2Data.artistName.trim(),
            phone: cleanPhone,
            role: selectedPlan === 'label_producer' ? 'label' : 'artist',
            plan: selectedPlan,
          },
        },
      });

      if (authError) throw authError;

      const userId = authData?.user?.id;
      if (!userId) throw new Error('User creation failed — no user ID returned');

      // 2. Guaranteed Admin Visibility: Register profile via secure server route
      try {
        await fetch('/api/auth/register-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: userId,
            email: step1Data.email.trim(),
            fullName: step1Data.fullName.trim(),
            artistName: step2Data.artistName.trim(),
            country: step2Data.country,
            phone: step2Data.phone,
            plan: selectedPlan,
          }),
        });
      } catch (apiErr) {
        console.warn('Server registration route notice:', apiErr);
      }

      // 3. Fallback Client-side Upsert for local reactivity
      await supabase.from('user_profiles').upsert(
        {
          id: userId,
          full_name: step1Data.fullName.trim(),
          email: step1Data.email.trim(),
          artist_name: step2Data.artistName.trim(),
          country: step2Data.country,
          phone: cleanPhone,
          role: selectedPlan === 'label_producer' ? 'label' : 'artist',
          plan: selectedPlan,
          status: 'ACTIVE',
          created_at: new Date().toISOString(),
        },
        { onConflict: 'id' }
      );

      const planTier = selectedPlan === 'free' ? 'FREE' : selectedPlan === 'artist_pro' ? 'PRO' : 'LABEL';
      await supabase.from('subscriptions').upsert(
        {
          user_id: userId,
          plan: selectedPlan,
          status: selectedPlan === 'free' ? 'active' : 'pending_payment',
          billing_cycle: 'yearly',
          plan_tier: planTier,
          created_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );

      toast.success('Account created! Redirecting to your dashboard...');
      router.push('/artist-dashboard');
    } catch (err: any) {
      console.error('Registration error:', err);
      toast.error(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { id: 'step-account', label: 'Account' },
    { id: 'step-profile', label: 'Profile' },
    { id: 'step-plan', label: 'Plan' },
    { id: 'step-verify', label: 'Verify' },
  ];

  const passwordValue = form1.watch('password', '');
  const passwordStrength = (pw: string) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };
  const strength = passwordStrength(passwordValue);
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['', 'var(--danger)', 'var(--warning)', 'var(--info)', 'var(--success)'];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
          Create your account
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Start distributing your music worldwide
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                  step > i + 1 ? 'step-complete' : step === i + 1 ? 'step-active' : 'step-inactive'
                }`}
              >
                {step > i + 1 ? (
                  <Icon name="CheckIcon" size={14} />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className="text-2xs mt-1 font-medium"
                style={{ color: step === i + 1 ? 'var(--primary-light)' : 'var(--muted-foreground)' }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="flex-1 h-px mx-2 mb-4"
                style={{
                  background: step > i + 1 ? 'var(--success)' : 'var(--border)',
                  transition: 'background 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Account Info */}
      {step === 1 && (
        <form onSubmit={form1.handleSubmit(handleStep1)} autoComplete="off" className="space-y-4 animate-slide-up">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Full name
            </label>
            <input
              type="text"
              autoComplete="off"
              {...form1.register('fullName', { required: 'Full name is required' })}
              className={`input-field ${form1.formState.errors.fullName ? 'input-field-error' : ''}`}
              placeholder="Your full legal name"
            />
            {form1.formState.errors.fullName && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form1.formState.errors.fullName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Email address
            </label>
            <input
              type="email"
              autoComplete="off"
              {...form1.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
              className={`input-field ${form1.formState.errors.email ? 'input-field-error' : ''}`}
              placeholder="you@example.com"
            />
            {form1.formState.errors.email && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form1.formState.errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Password
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              Minimum 8 characters with uppercase, number, and special character.
            </p>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...form1.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Minimum 8 characters' },
                  pattern: {
                    value: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
                    message: 'Must include uppercase, number, and special character',
                  },
                })}
                className={`input-field pr-10 ${form1.formState.errors.password ? 'input-field-error' : ''}`}
                placeholder="••••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 btn-ghost p-0"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
              </button>
            </div>
            {passwordValue && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex gap-1 flex-1">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={`strength-bar-${n}`}
                      className="h-1 flex-1 rounded-full transition-all duration-300"
                      style={{
                        background: n <= strength ? strengthColors[strength] : 'var(--border)',
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs font-medium" style={{ color: strengthColors[strength] }}>
                  {strengthLabels[strength]}
                </span>
              </div>
            )}
            {form1.formState.errors.password && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form1.formState.errors.password.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Confirm password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              {...form1.register('confirmPassword', { required: 'Please confirm your password' })}
              className={`input-field ${form1.formState.errors.confirmPassword ? 'input-field-error' : ''}`}
              placeholder="••••••••••"
            />
            {form1.formState.errors.confirmPassword && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form1.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          <button type="submit" className="btn-primary w-full mt-2" style={{ height: '44px' }}>
            Continue to Artist Profile
            <Icon name="ArrowRightIcon" size={16} />
          </button>
        </form>
      )}

      {/* Step 2: Artist Details */}
      {step === 2 && (
        <form onSubmit={form2.handleSubmit(handleStep2)} autoComplete="off" className="space-y-4 animate-slide-up">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Artist / Stage name
            </label>
            <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
              The name that will appear on streaming platforms.
            </p>
            <input
              type="text"
              autoComplete="off"
              {...form2.register('artistName', { required: 'Artist name is required' })}
              className={`input-field ${form2.formState.errors.artistName ? 'input-field-error' : ''}`}
              placeholder="Your artist name"
            />
            {form2.formState.errors.artistName && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form2.formState.errors.artistName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Country of residence
            </label>
            <div className="relative">
              <select
                {...form2.register('country', { required: 'Country is required' })}
                className={`select-field ${form2.formState.errors.country ? 'input-field-error' : ''}`}
              >
                {COUNTRIES.map((c) => (
                  <option key={`country-${c}`} value={c}>{c}</option>
                ))}
              </select>
              <Icon name="ChevronDownIcon" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
              Mobile Phone Number (Compulsory)
            </label>
            <div className="flex gap-2">
              <div
                className="px-3.5 py-2.5 rounded-xl text-sm font-bold flex items-center select-none"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
              >
                +91
              </div>
              <input
                type="tel"
                autoComplete="off"
                maxLength={10}
                {...form2.register('phone', {
                  required: 'Mobile number is compulsory',
                  pattern: { value: /^[0-9]{10}$/, message: 'Enter a valid 10-digit mobile number' }
                })}
                className={`input-field flex-1 ${form2.formState.errors.phone ? 'input-field-error' : ''}`}
                placeholder="9876543210"
              />
            </div>
            {form2.formState.errors.phone && (
              <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                {form2.formState.errors.phone.message}
              </p>
            )}
            <p className="text-2xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Required for payout verification and critical release alerts.
            </p>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="btn-secondary flex-1"
              style={{ height: '44px' }}
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Back
            </button>
            <button type="submit" className="btn-primary flex-1" style={{ height: '44px' }}>
              Choose Plan
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Fixed INR Plan Selection */}
      {step === 3 && (
        <div className="animate-slide-up">
          <p className="text-xs mb-3 text-center" style={{ color: 'var(--muted-foreground)' }}>
            Transparent Indian Pricing in <span className="font-semibold" style={{ color: 'var(--primary-light)' }}>INR (₹)</span>
          </p>
          <div className="space-y-3 mb-6">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className="relative flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200"
                style={{
                  background: selectedPlan === plan.id ? 'rgba(124, 58, 237, 0.1)' : 'var(--muted)',
                  border: `2px solid ${selectedPlan === plan.id ? 'var(--primary)' : 'var(--border)'}`,
                  boxShadow: selectedPlan === plan.id ? '0 0 12px rgba(124,58,237,0.15)' : 'none',
                }}
              >
                {plan.popular && (
                  <span
                    className="absolute -top-2.5 left-4 text-2xs font-bold px-2.5 py-0.5 rounded-full"
                    style={{ background: 'var(--primary)', color: 'white' }}
                  >
                    Most Popular
                  </span>
                )}
                <div
                  className="w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
                  style={{
                    borderColor: selectedPlan === plan.id ? 'var(--primary)' : 'var(--border-light)',
                    background: selectedPlan === plan.id ? 'var(--primary)' : 'transparent',
                  }}
                >
                  {selectedPlan === plan.id && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
                      {plan.name}
                    </span>
                    <div className="text-right">
                      <span className="font-bold font-mono-data text-sm" style={{ color: plan.color }}>
                        {plan.price}
                        <span className="text-xs font-normal ml-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {plan.period}
                        </span>
                      </span>
                    </div>
                  </div>
                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    {plan.releases}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {plan.features.map((f) => (
                      <span
                        key={`${plan.id}-feat-${f}`}
                        className="text-2xs px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--card)', color: 'var(--secondary-foreground)', border: '1px solid var(--border)' }}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="btn-secondary flex-1"
              style={{ height: '44px' }}
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Back
            </button>
            <button
              type="button"
              onClick={handleStep3}
              className="btn-primary flex-1"
              style={{ height: '44px' }}
            >
              Continue
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Agreements & Submit */}
      {step === 4 && (
        <div className="animate-slide-up space-y-5">
          <div
            className="p-4 rounded-xl space-y-3"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              Account Summary
            </p>
            <div className="space-y-1.5">
              {[
                { label: 'Name', value: step1Data?.fullName },
                { label: 'Email', value: step1Data?.email },
                { label: 'Artist Name', value: step2Data?.artistName },
                { label: 'Country', value: step2Data?.country },
                { label: 'Mobile', value: step2Data?.phone ? `+91 ${step2Data.phone}` : '—' },
                {
                  label: 'Selected Plan',
                  value: (() => {
                    const p = PLANS.find((pl) => pl.id === selectedPlan);
                    if (!p) return '—';
                    return `${p.name} (${p.price} ${p.period})`;
                  })(),
                },
              ].map((row) => (
                <div key={`summary-${row.label}`} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{row.label}</span>
                  <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{row.value || '—'}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="checkbox-custom mt-0.5"
              />
              <span className="text-sm leading-relaxed" style={{ color: 'var(--secondary-foreground)' }}>
                I agree to TubeNexa&apos;s{' '}
                <span className="underline" style={{ color: 'var(--primary-light)' }}>Terms of Service</span>
                {' '}and{' '}
                <span className="underline" style={{ color: 'var(--primary-light)' }}>Privacy Policy</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedDist}
                onChange={(e) => setAgreedDist(e.target.checked)}
                className="checkbox-custom mt-0.5"
              />
              <span className="text-sm leading-relaxed" style={{ color: 'var(--secondary-foreground)' }}>
                I agree to the{' '}
                <span className="underline" style={{ color: 'var(--primary-light)' }}>TubeNexa Distribution Agreement</span>
                {' '}and{' '}
                <span className="underline" style={{ color: 'var(--primary-light)' }}>Content Guidelines</span>
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(3)}
              className="btn-secondary flex-1"
              style={{ height: '44px' }}
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Back
            </button>
            <button
              type="button"
              onClick={handleFinalSubmit}
              disabled={isLoading || !agreedTerms || !agreedDist}
              className="btn-primary flex-1"
              style={{ height: '44px' }}
            >
              {isLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  <Icon name="CheckIcon" size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <p className="text-center text-sm mt-6" style={{ color: 'var(--muted-foreground)' }}>
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="font-semibold hover:underline"
          style={{ color: 'var(--primary-light)' }}
        >
          Sign in
        </button>
      </p>

      <style jsx global>{`
        /* Strip yellow browser autofill and retain theme background */
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #13131a inset !important;
          -webkit-text-fill-color: #ffffff !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
}